import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateJobTaskMetadata } from "@labeling-tools/api/lebron/react-query-hooks";
import { auth } from "@/utils/auth";

interface ReviewableQaTask {
  id?: string | null;
  result?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
}

/**
 * When a labeler opens a QA task flagged for their review, stamp
 * labeler_reviewed_at so it drops off the Needs-Review queue and decrements
 * the badge. Gated to the task's own original labeler
 * (metadata.source_task_user) so a team lead/admin browsing someone else's
 * Needs Review tab doesn't mark it reviewed for them.
 */
export function useMarkQaTaskReviewed(task: ReviewableQaTask | undefined) {
  const queryClient = useQueryClient();
  const { mutate: updateTaskMetadata } = useUpdateJobTaskMetadata(
    task?.id ?? "",
  );

  const myEmail = auth.currentUser?.email?.toLowerCase();
  const isOwnTask =
    !!myEmail &&
    (task?.metadata?.source_task_user as string | undefined)?.toLowerCase() ===
      myEmail;

  useEffect(() => {
    if (
      isOwnTask &&
      task?.result?.flag_labeler === true &&
      !task?.metadata?.labeler_reviewed_at
    ) {
      updateTaskMetadata(
        { labeler_reviewed_at: new Date().toISOString() },
        {
          onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: ["all-job-tasks"] }),
        },
      );
    }
    // Keyed on the task alone so it fires once per opened task.
  }, [task?.id]);
}
